import { VideoEditor } from '../VideoEditor';
import { TimelineMode } from './TimelineMode';
import {
  AbstractElementCommand,
  AffectedElementOptions,
  RippleOptions,
} from './AbstractCommand';
import { Element } from '../models/Element';
import { TrackManager } from '../lib/TrackManager';

/**
 * Command to adjust the end of an element (both extending and trimming)
 * Handles both shrinking and extending the duration by changing the element's end time
 *
 * This command replaces the functionality of both the former TrimElementEndCommand
 * and ExtendElementCommand, unifying them into a single implementation that handles
 * both cases:
 * - Extending mode: When new end time > current end time
 * - Trimming mode: When new end time < current end time
 *
 * The command detects automatically which behavior to use based on the comparison
 * between the current element end time and the requested new end time.
 *
 * Features:
 * - Frame snapping of the end time based on editor framerate
 * - Full ripple mode support to push or pull subsequent elements
 * - Video-specific property handling (trim_duration)
 * - State capture for undo/redo
 *
 * Uses direct object references
 */
export class TrimElementEndCommand extends AbstractElementCommand {
  private newEndTime: number;
  private originalState: Record<string, any>;
  private originalEndTime: number;
  private trackManager: TrackManager;

  /**
   * Creates a new TrimElementEndCommand
   * @param editor The video editor instance
   * @param element The element to trim
   * @param newEndTime The new end time for the element (in seconds)
   */
  constructor(editor: VideoEditor, element: Element, newEndTime: number) {
    super(editor, element);

    // Get TrackManager from the editor instead of creating a new one
    this.trackManager = this.editor.getTrackManager();

    // Apply frame snapping to the end time
    this.newEndTime = this.snapTimeToFrame(newEndTime);

    // Capture full element state for undo
    this.originalState = this.captureElementState(this.sourceElement);
    this.originalEndTime =
      this.sourceElement.time +
      (typeof this.sourceElement.duration === 'number'
        ? this.sourceElement.duration
        : 0);

    // Store affected elements for undo in ripple mode
    if (this.getTimelineMode() === TimelineMode.RIPPLE) {
      this.storeAffectedElements({});
    }
  }

  execute(): boolean {
    // Use the trackManager instance with direct object reference
    const result = this.trackManager.trimElementEnd(
      this.sourceElement,
      this.newEndTime,
      this.getTimelineMode(), // Pass the mode explicitly
    );

    if (result) {
      // Recalculate timeline duration after successful trim
      this.recalculateTimeline();
    }

    return result;
  }

  undo(): boolean {
    // Restore the complete original element state
    this.restoreElementState(this.sourceElement, this.originalState);

    // Restore affected elements if in ripple mode
    this.restoreAffectedElements();

    // Recalculate timeline duration
    this.recalculateTimeline();

    return true;
  }

  getDescription(): string {
    // Change description based on whether extending or trimming
    const currentEndTime =
      this.sourceElement.time +
      (typeof this.sourceElement.duration === 'number'
        ? this.sourceElement.duration
        : 0);

    if (this.newEndTime > currentEndTime) {
      return `Extend element ${this.sourceElement.id} to ${this.newEndTime}s`;
    }
    return `Trim end of element ${this.sourceElement.id} to ${this.newEndTime}s`;
  }
}
